import { z } from "zod";
import { DatabaseSequenceSchema, OpaqueCursorSchema, PageLimitSchema, DecimalStringSchema, UuidSchema, } from "./primitives.js";
export const GraphNodeTypeSchema = z.enum([
    "note",
    "project",
    "task",
    "document",
    "focus_thread",
    "research_source",
    "account",
]);
export const EntityLinkTypeSchema = z.enum(["references", "related_to", "derived_from", "supports"]);
export const GraphEdgeTypeSchema = z.union([EntityLinkTypeSchema, z.literal("assigned_to")]);
const EntityEndpointSchema = z
    .object({
    type: GraphNodeTypeSchema,
    id: UuidSchema,
    revision: DatabaseSequenceSchema,
})
    .strict();
export const EntityLinkCommandSchema = z.discriminatedUnion("action", [
    z
        .object({
        action: z.literal("create"),
        commandId: UuidSchema,
        linkId: UuidSchema,
        workspaceId: UuidSchema,
        linkType: EntityLinkTypeSchema,
        source: EntityEndpointSchema,
        target: EntityEndpointSchema,
        sourceMessageId: UuidSchema.nullable(),
    })
        .strict(),
    z
        .object({
        action: z.literal("remove"),
        commandId: UuidSchema,
        linkId: UuidSchema,
        workspaceId: UuidSchema,
        revision: DatabaseSequenceSchema,
    })
        .strict(),
]);
export const EntityLinkProjectionSchema = z
    .object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    linkType: EntityLinkTypeSchema,
    source: EntityEndpointSchema,
    target: EntityEndpointSchema,
    sourceMessageId: UuidSchema.nullable(),
    revision: DatabaseSequenceSchema,
    removedAt: z.string().datetime({ offset: true }).nullable(),
})
    .strict();
export const GraphEdgeSchema = EntityLinkProjectionSchema.extend({ linkType: GraphEdgeTypeSchema });
export const GraphNodeSchema = z
    .object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    type: GraphNodeTypeSchema,
    label: z.string().min(1).max(500),
    revision: DatabaseSequenceSchema,
    archived: z.boolean(),
    tags: z.array(z.string().max(100)).max(100),
    projectId: UuidSchema.nullable(),
    ownerAccountId: UuidSchema.nullable(),
})
    .strict();
export const GraphResultSchema = z
    .object({
    nodes: z.array(GraphNodeSchema).max(250),
    edges: z.array(GraphEdgeSchema).max(250),
    loadedCount: z.number().int().nonnegative(),
    availableCount: z.number().int().nonnegative(),
    truncated: z.boolean(),
    nextPageCursor: OpaqueCursorSchema.nullable(),
})
    .strict();
export const ObsidianPreviewItemSchema = z
    .object({
    path: z.string().min(1).max(1000),
    action: z.enum(["create", "update", "conflict", "skip"]),
    noteId: UuidSchema.nullable(),
    expectedRevision: DatabaseSequenceSchema.nullable(),
    title: z.string().max(500).nullable(),
    diagnostics: z.array(z.string().max(500)).max(50),
})
    .strict();
export const ObsidianPreviewSchema = z
    .object({
    items: z.array(ObsidianPreviewItemSchema).max(10_000),
    diagnostics: z.array(z.string().max(500)).max(100),
})
    .strict();
export const ObsidianApplyResultSchema = z
    .object({
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    conflicts: z.number().int().nonnegative(),
    preview: ObsidianPreviewSchema,
})
    .strict();
export const GraphRootReferenceSchema = z
    .object({
    type: GraphNodeTypeSchema,
    id: UuidSchema,
    revision: DatabaseSequenceSchema,
})
    .strict();
export const GraphRootSearchCommandSchema = z
    .object({
    workspaceId: UuidSchema,
    query: z.string().trim().min(1).max(500),
    nodeTypes: z.array(GraphNodeTypeSchema).min(1).max(7),
    cursor: OpaqueCursorSchema.nullable().default(null),
    limit: PageLimitSchema.default(50),
})
    .strict();
export const GraphRootSearchResultSchema = z
    .object({
    items: z.array(GraphNodeSchema).max(250),
    nextCursor: OpaqueCursorSchema.nullable(),
    truncated: z.boolean(),
})
    .strict();
export const GraphTraversalQuerySchema = z
    .object({
    workspaceId: UuidSchema,
    root: GraphRootReferenceSchema,
    nodeTypes: z.array(GraphNodeTypeSchema).max(7),
    edgeTypes: z.array(GraphEdgeTypeSchema).max(5),
    projectId: UuidSchema.nullable(),
    accountId: UuidSchema.nullable(),
    tag: z.string().max(100).nullable(),
    includeArchived: z.boolean(),
    cursor: OpaqueCursorSchema.nullable().default(null),
    limit: PageLimitSchema.default(50),
})
    .strict();
//# sourceMappingURL=graph.js.map